import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminActivities, type AdminActivityDetail, themes as themesApi, type ThemeSummary } from '../api/client'
import PageHeader from './PageHeader'
import { COLORS, RADIUS, SHADOWS, FONT } from '../styles/tokens'
import { CATEGORY_COLORS } from './charts/activityColors'
import { es } from '../i18n/es'

const CATEGORIES = Object.keys(CATEGORY_COLORS)
const CATEGORY_LABELS = es.stats.categories

type Tier = 'BASIC' | 'PLUS' | 'PREMIUM'

const TIERS: Tier[] = ['BASIC', 'PLUS', 'PREMIUM']

const TIER_COLORS: Record<Tier, { bg: string; text: string; border: string }> = {
  BASIC:   { bg: '#EDF6EE', text: '#2D6A4F', border: '#8FC89A' },
  PLUS:    { bg: '#FFF8EE', text: '#7A4F2D', border: '#E8C87A' },
  PREMIUM: { bg: '#F3EEFF', text: '#5B3A9E', border: '#B49FD8' },
}

const GAME_EMOJIS: Record<string, string> = {
  memory:       '🃏',
  wordmatch:    '📖',
  cutlery:      '🍴',
  wordsearch:   '🔍',
  sudoku:       '🔢',
  maths:        '🧮',
  numbers:      '🔢',
  words:        '📝',
  shoppinglist: '🛒',
}

interface AdminActivityDetailPageProps {
  slug:    string
  onBack:  () => void
}

export default function AdminActivityDetailPage({ slug, onBack }: AdminActivityDetailPageProps) {
  const navigate = useNavigate()
  const [activity,          setActivity]          = useState<AdminActivityDetail | null>(null)
  const [loading,           setLoading]           = useState(true)
  const [saving,            setSaving]            = useState(false)
  const [error,             setError]             = useState<string | null>(null)
  const [saved,             setSaved]             = useState(false)
  const [tier,              setTier]              = useState<Tier>('BASIC')
  const [isPublished,       setIsPublished]       = useState(false)
  const [category,          setCategory]          = useState('')
  const [usesGeneralThemes, setUsesGeneralThemes] = useState(false)
  const [allowedThemeIds,   setAllowedThemeIds]   = useState<number[]>([])
  const [allThemes,         setAllThemes]         = useState<ThemeSummary[]>([])

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([
      adminActivities.get(slug),
      themesApi.listAll(),
    ])
      .then(([data, themes]) => {
        setActivity(data)
        setTier(data.requiredTier)
        setIsPublished(data.isPublished)
        setCategory(data.category ?? '')
        setUsesGeneralThemes(data.usesGeneralThemes)
        setAllowedThemeIds(data.allowedThemeIds)
        setAllThemes(themes)
      })
      .catch(() => setError('Error al cargar la actividad'))
      .finally(() => setLoading(false))
  }, [slug])

  useEffect(() => { load() }, [load])

  const handleSave = async (updates: {
    requiredTier?:      Tier
    isPublished?:       boolean
    category?:          string | null
    usesGeneralThemes?: boolean
    allowedThemeIds?:   number[]
  }) => {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const updated = await adminActivities.patch(slug, updates)
      setActivity(updated)
      if (updated.requiredTier      !== undefined) setTier(updated.requiredTier)
      if (updated.isPublished       !== undefined) setIsPublished(updated.isPublished)
      if (updated.category          !== undefined) setCategory(updated.category ?? '')
      if (updated.usesGeneralThemes !== undefined) setUsesGeneralThemes(updated.usesGeneralThemes)
      if (updated.allowedThemeIds   !== undefined) setAllowedThemeIds(updated.allowedThemeIds)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch {
      setError('Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const handleTierChange = async (newTier: Tier) => {
    if (newTier === tier) return
    await handleSave({ requiredTier: newTier })
  }

  const handleTogglePublished = async () => {
    await handleSave({ isPublished: !isPublished })
  }

  const handleCategoryChange = (newCategory: string) => {
    setCategory(newCategory)
  }

  const handleCategorySave = async () => {
    await handleSave({ category: category || null })
  }

  const handleToggleUsesGeneralThemes = async () => {
    await handleSave({ usesGeneralThemes: !usesGeneralThemes })
  }

  const handleToggleTheme = async (themeId: number) => {
    const next = allowedThemeIds.includes(themeId)
      ? allowedThemeIds.filter(id => id !== themeId)
      : [...allowedThemeIds, themeId]
    setAllowedThemeIds(next)
    await handleSave({ allowedThemeIds: next })
  }

  if (loading) {
    return (
      <div style={s.page}>
        <PageHeader onBack={onBack} backLabel="← Actividades" title="Cargando…" />
      </div>
    )
  }

  if (error && !activity) {
    return (
      <div style={s.page}>
        <PageHeader onBack={onBack} backLabel="← Actividades" title="Error" />
        <div style={s.content}>
          <p style={s.errorText}>{error}</p>
        </div>
      </div>
    )
  }

  if (!activity) return null

  const emoji = GAME_EMOJIS[slug] ?? '🎮'

  return (
    <div style={s.page}>
      <PageHeader onBack={onBack} backLabel="← Actividades" title={`${emoji} ${slug}`} />

      <div style={s.content}>
        {error && <div style={s.errorBanner}>{error}</div>}
        {saved && <div style={s.savedBanner}>✓ Guardado</div>}

        {/* Tier section */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Tier requerido</h2>
          <div style={s.tierBtns}>
            {TIERS.map((t) => {
              const active = tier === t
              const colors = TIER_COLORS[t]
              return (
                <button
                  key={t}
                  onClick={() => handleTierChange(t)}
                  disabled={saving}
                  style={{
                    ...s.tierBtn,
                    backgroundColor: active ? colors.bg : COLORS.bgCard,
                    color:           active ? colors.text : COLORS.textMuted,
                    borderColor:     active ? colors.border : COLORS.borderWarm,
                    fontWeight:      active ? 700 : 500,
                  }}
                  aria-pressed={active}
                >
                  {t}
                </button>
              )
            })}
          </div>
        </div>

        {/* Published section */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Visible</h2>
          <button
            onClick={handleTogglePublished}
            disabled={saving}
            style={{
              ...s.toggleBtn,
              backgroundColor: isPublished ? '#E8F5EE' : COLORS.bgPage,
              color:           isPublished ? COLORS.green : COLORS.textMuted,
              borderColor:     isPublished ? COLORS.greenPale : COLORS.borderWarm,
            }}
            aria-pressed={isPublished}
          >
            {isPublished ? '✓ Sí' : '✗ No'}
          </button>
        </div>

        {/* Uses general themes */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Usa temas de imágenes</h2>
          <button
            onClick={handleToggleUsesGeneralThemes}
            disabled={saving}
            style={{
              ...s.toggleBtn,
              backgroundColor: usesGeneralThemes ? '#E8F5EE' : COLORS.bgPage,
              color:           usesGeneralThemes ? COLORS.green : COLORS.textMuted,
              borderColor:     usesGeneralThemes ? COLORS.greenPale : COLORS.borderWarm,
            }}
            aria-pressed={usesGeneralThemes}
          >
            {usesGeneralThemes ? '✓ Sí' : '✗ No'}
          </button>
        </div>

        {/* Allowed themes multi-select (only when usesGeneralThemes) */}
        {usesGeneralThemes && (
          <div style={s.section}>
            <h2 style={s.sectionTitle}>Temas permitidos</h2>
            <p style={s.sectionNote}>Los nuevos temas se añaden automáticamente. Desmarca para excluir.</p>
            <div style={s.themeGrid}>
              {allThemes.map((theme) => {
                const allowed = allowedThemeIds.includes(theme.id)
                return (
                  <label key={theme.id} style={s.themeLabel}>
                    <input
                      type="checkbox"
                      checked={allowed}
                      onChange={() => handleToggleTheme(theme.id)}
                      disabled={saving}
                      style={s.themeCheckbox}
                    />
                    <span style={s.themeName}>
                      {theme.nameEs}
                      {!theme.published && <span style={s.unpublishedBadge}> (no pub.)</span>}
                    </span>
                  </label>
                )
              })}
              {allThemes.length === 0 && (
                <p style={s.noContent}>No hay temas creados aún.</p>
              )}
            </div>
          </div>
        )}

        {/* Category section */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Categoría</h2>
          <div style={s.categoryRow}>
            <select
              value={category}
              onChange={(e) => { handleCategoryChange(e.target.value); }}
              style={s.select}
              disabled={saving}
            >
              <option value="">— Sin categoría —</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{CATEGORY_LABELS[cat] ?? cat}</option>
              ))}
            </select>
            <button
              onClick={handleCategorySave}
              disabled={saving}
              style={s.saveCatBtn}
            >
              {saving ? '…' : 'Guardar'}
            </button>
          </div>
        </div>

        {/* Content editor links */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Editar contenido</h2>
          <div style={s.editorLinks}>
            {(slug === 'memory' || slug === 'wordmatch' || slug === 'wordsearch') && (
              <button
                onClick={() => navigate('/admin/classic')}
                style={s.editorBtn}
              >
                🃏 Tarjetas clásicas
              </button>
            )}
            {slug === 'attention' && (
              <button
                onClick={() => navigate('/admin/words')}
                style={s.editorBtn}
              >
                📝 Words
              </button>
            )}
            {slug === 'crosswords' && (
              <button
                onClick={() => navigate('/admin/words')}
                style={s.editorBtn}
              >
                📝 Words
              </button>
            )}
            {slug === 'synonyms' && (
              <button
                onClick={() => navigate('/admin/synonyms')}
                style={s.editorBtn}
              >
                📚 Sinónimos y Antónimos
              </button>
            )}
            {!((slug === 'memory' || slug === 'wordmatch' || slug === 'wordsearch') || slug === 'attention' || slug === 'crosswords' || slug === 'synonyms') && (
              <p style={s.noContent}>Sin contenido editable</p>
            )}
          </div>
        </div>

        {/* Metadata */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Información</h2>
          <div style={s.metaRow}>
            <span style={s.metaLabel}>Slug:</span>
            <span style={s.metaValue}>{activity.slug}</span>
          </div>
          <div style={s.metaRow}>
            <span style={s.metaLabel}>Orden:</span>
            <span style={s.metaValue}>{activity.sortOrder}</span>
          </div>
          <div style={s.metaRow}>
            <span style={s.metaLabel}>Actualizado:</span>
            <span style={s.metaValue}>
              {new Date(activity.updatedAt).toLocaleDateString('es-ES', {
                day: '2-digit', month: 'short', year: 'numeric',
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight:      '100vh',
    display:        'flex',
    flexDirection:  'column',
    background:     COLORS.bgPage,
  },
  content: {
    flex:      1,
    padding:   '24px 16px',
    maxWidth:  600,
    width:     '100%',
    margin:    '0 auto',
    boxSizing: 'border-box',
  },
  errorText: {
    color:     COLORS.errorText,
    fontSize:  FONT.size.base,
  },
  errorBanner: {
    backgroundColor: COLORS.errorBg,
    color:           COLORS.errorText,
    border:          `1px solid ${COLORS.errorText}`,
    borderRadius:    RADIUS.md,
    padding:         '12px 16px',
    marginBottom:    16,
    fontSize:        FONT.size.base,
  },
  savedBanner: {
    backgroundColor: COLORS.greenSurface,
    color:           COLORS.green,
    border:          `1px solid ${COLORS.greenPale}`,
    borderRadius:    RADIUS.md,
    padding:         '12px 16px',
    marginBottom:    16,
    fontSize:        FONT.size.base,
    fontWeight:      700,
  },

  section: {
    background:    COLORS.bgCard,
    borderRadius:  RADIUS.lg,
    border:        `1.5px solid ${COLORS.borderWarm}`,
    boxShadow:     SHADOWS.subtle,
    padding:       '20px 16px',
    marginBottom:  16,
  },
  sectionTitle: {
    fontSize:   FONT.size.lg,
    fontWeight: 700,
    color:      COLORS.textPrimary,
    margin:     '0 0 12px 0',
  },

  tierBtns: {
    display: 'flex',
    gap:     8,
    flexWrap: 'wrap',
  },
  tierBtn: {
    minHeight:    48,
    padding:      '0 20px',
    fontSize:     FONT.size.base,
    letterSpacing: '0.04em',
    border:       '1.5px solid',
    borderRadius: RADIUS.md,
    cursor:       'pointer',
    transition:   'all 0.15s',
  },

  toggleBtn: {
    minHeight:    48,
    minWidth:     96,
    padding:      '0 16px',
    fontSize:     FONT.size.base,
    fontWeight:   700,
    border:       '1.5px solid',
    borderRadius: RADIUS.md,
    cursor:       'pointer',
    transition:   'all 0.15s',
  },

  categoryRow: {
    display: 'flex',
    gap:     8,
    alignItems: 'center',
  },
  select: {
    flex:         1,
    minHeight:    48,
    padding:      '0 12px',
    fontSize:     FONT.size.base,
    border:       `1.5px solid ${COLORS.borderWarm}`,
    borderRadius: RADIUS.sm,
    background:   COLORS.bgCard,
    color:        COLORS.textPrimary,
    boxSizing:    'border-box' as const,
    cursor:       'pointer',
  },
  saveCatBtn: {
    minHeight:    48,
    padding:      '0 20px',
    fontSize:     FONT.size.base,
    fontWeight:   700,
    color:        COLORS.bgCard,
    background:   COLORS.green,
    border:       'none',
    borderRadius: RADIUS.sm,
    cursor:       'pointer',
  },

  editorLinks: {
    display: 'flex',
    gap:     8,
    flexWrap: 'wrap',
  },
  editorBtn: {
    minHeight:    48,
    padding:      '0 16px',
    fontSize:     FONT.size.base,
    fontWeight:   700,
    color:        COLORS.green,
    background:   COLORS.greenSurface,
    border:       `1.5px solid ${COLORS.greenPale}`,
    borderRadius: RADIUS.md,
    cursor:       'pointer',
  },
  noContent: {
    color:     COLORS.textMuted,
    fontSize:  FONT.size.base,
    fontStyle: 'italic',
    margin:    0,
  },

  sectionNote: {
    fontSize:    FONT.size.sm,
    color:       COLORS.textMuted,
    margin:      '0 0 12px 0',
    lineHeight:  1.4,
  },
  themeGrid: {
    display:   'flex',
    flexWrap:  'wrap' as const,
    gap:       '8px 16px',
  },
  themeLabel: {
    display:    'flex',
    alignItems: 'center',
    gap:        6,
    cursor:     'pointer',
    minHeight:  32,
    userSelect: 'none' as const,
  },
  themeCheckbox: {
    width:  18,
    height: 18,
    cursor: 'pointer',
    flexShrink: 0,
  },
  themeName: {
    fontSize:   FONT.size.base,
    color:      COLORS.textBody,
  },
  unpublishedBadge: {
    fontSize:  FONT.size.sm,
    color:     COLORS.textMuted,
    fontStyle: 'italic' as const,
  },

  metaRow: {
    display:       'flex',
    justifyContent: 'space-between',
    padding:       '8px 0',
    borderBottom:  `1px solid ${COLORS.borderWarm}`,
  },
  metaLabel: {
    fontSize:   FONT.size.base,
    fontWeight: 600,
    color:      COLORS.textBody,
  },
  metaValue: {
    fontSize:  FONT.size.base,
    color:     COLORS.textSecondary,
    fontFamily: 'monospace',
  },
}
